import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import cx from 'classnames';
import { transformNavigationLink } from 'helpers/customNavigationRules';
import PropTypes from 'prop-types';

import { useApplicationState, useSettings } from '@atlas/core';
import { Icon } from '@atlas/ui';

import useTranslate from 'common/I18n/Interpreter';
import { CDN_TYPE_CMS, imageUrlCDN } from 'common/utils/utils';
import useSignInRedirects from 'components/Auth/useSignInRedirects';
import { MENU_ITEM_COUNTER_COMPONENT, MENU_ITEMS_WITH_COUNTER } from './constants';
import { urlCanParse } from 'src/helpers/urlCanParse';

import VirtualGameModal from '../../../VirtualGame/VirtualGameModal';

const MenuListItem = ({
  activeItem = null,
  altIcon,
  authRequired = false,
  icon,
  iconType,
  isSupportIcon,
  link = null,
  name,
  noLink,
  onClick,
  subpages,
}) => {
  const { redirectToSignIn } = useSignInRedirects();
  const { authenticated } = useApplicationState();
  const t = useTranslate();
  const { theme } = useSettings();
  const isDarkMode = theme === 'dark';
  const [subpagesModalOpen, setSubpagesModalOpen] = useState(false);
  const [forceDefaultIcon, setForceDefaultIcon] = useState(false);

  const realLink = transformNavigationLink(link);

  const subpagesModalHandler = () => {
    setSubpagesModalOpen(!subpagesModalOpen);
  };

  const onClickHandler = useCallback(
    e => {
      if (authRequired && !authenticated) {
        e.preventDefault();
        redirectToSignIn({ redirectPath: realLink });
      }
    },
    [authRequired, authenticated, realLink, redirectToSignIn],
  );

  const CounterComponent = useMemo(() => {
    if (MENU_ITEMS_WITH_COUNTER.includes(name)) {
      return MENU_ITEM_COUNTER_COMPONENT[name];
    }

    return null;
  }, [name]);

  const sportsItemClassName = cx('button-menu', { active: activeItem === name });

  const IconEl = useMemo(() => {
    if (iconType === 'custom' && !forceDefaultIcon) {
      const url = isDarkMode ? altIcon || icon : icon || altIcon;

      return (
        <>
          <div className="menu-custom-icon">
            <img
              alt="menu-item"
              onError={() => setForceDefaultIcon(true)}
              src={imageUrlCDN(isSupportIcon ? url : icon, CDN_TYPE_CMS)}
            />
            {CounterComponent && <CounterComponent />}
          </div>
          <span className="label">{t(name)}</span>
        </>
      );
    }

    return (
      <>
        <div className="menu-icon">
          <Icon height={36} name={`colored-${forceDefaultIcon ? 'i-casino' : icon}`} width={36} />
          {CounterComponent && <CounterComponent />}
        </div>
        <span className="label">{t(name)}</span>
      </>
    );
  }, [
    iconType,
    forceDefaultIcon,
    icon,
    CounterComponent,
    t,
    name,
    isDarkMode,
    altIcon,
    isSupportIcon,
  ]);

  if (noLink) {
    return (
      <div className={sportsItemClassName} onClick={onClick}>
        {IconEl}
      </div>
    );
  }

  return !link || urlCanParse(link) ? (
    <a
      className={sportsItemClassName}
      href={link}
      onClick={() => onClick(name)}
      rel="noreferrer"
      target="_blank"
    >
      {IconEl}
    </a>
  ) : (
    <>
      <NavLink className={sportsItemClassName} onClick={onClickHandler} to={realLink}>
        {IconEl}
      </NavLink>

      {subpages && subpages.length > 0 && subpagesModalOpen && (
        <VirtualGameModal
          name={name}
          subpages={subpages}
          subpagesModalHandler={subpagesModalHandler}
        />
      )}
    </>
  );
};

MenuListItem.propTypes = {
  activeItem: PropTypes.string,
  icon: PropTypes.string.isRequired,
  iconType: PropTypes.string.isRequired,
  link: PropTypes.string,
  name: PropTypes.string.isRequired,
  noLink: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default MenuListItem;
